// ============================================================================
// Floor-machine status vocabulary — the ONE place it is written down.
//
// Mirrors the CHECK constraint floor_machines_status_check added 2026-08-14.
// Before that migration the column was free text and the vocabulary was
// whatever two strings happened to be in it ('Active', 'Parts Only'), with no
// way to say "broken" — which is half of what a QR scan is for.
//
// VEHICLES ARE A DIFFERENT VOCABULARY. vehicles.status keeps its own values
// and its own reader (isActiveVehicle / INACTIVE_STATUSES in use-fleet-data).
// Do not point one at the other.
//
// There is deliberately no 'missing' status. "Nobody has physically confirmed
// this machine in 90 days" is DERIVED from last_confirmed_at (see staleness
// below), exactly as vehicle compliance chips are derived from dates and never
// stored. A stored 'missing' goes stale the moment somebody finds the machine.
// ============================================================================

import type { ComplianceLevel } from './compliance';

export const MACHINE_STATUSES = [
  'in_service',
  'needs_repair',
  'out_for_repair',
  'parts_only',
  'retired',
] as const;

export type MachineStatus = (typeof MACHINE_STATUSES)[number];

const LABELS: Record<MachineStatus, string> = {
  in_service: 'In service',
  needs_repair: 'Needs repair',
  out_for_repair: 'Out for repair',
  parts_only: 'Parts only',
  retired: 'Retired',
};

/** Human label for a status. Unknown/legacy values are shown verbatim so a
 *  stale row is visible rather than silently rendered as blank. */
export function statusLabel(status: string | null | undefined): string {
  if (!status) return '—';
  return LABELS[status as MachineStatus] ?? status;
}

/** Reuses the compliance chip palette so equipment and vehicles look alike. */
export function statusTone(status: string | null | undefined): ComplianceLevel {
  switch (status) {
    case 'in_service':
      return 'current';
    case 'needs_repair':
    case 'out_for_repair':
      return 'expired';
    case 'parts_only':
      return 'amber';
    case 'retired':
      return 'inactive';
    default:
      return 'missing';
  }
}

/** A machine that is broken or on its way to a repair shop. */
export function isBroken(status: string | null | undefined): boolean {
  return status === 'needs_repair' || status === 'out_for_repair';
}

/** Counts toward "machines we have". parts_only is not a machine (FM-0020 is
 *  cannibalised), and retired is gone. */
export function isCountableMachine(status: string | null | undefined): boolean {
  return status !== 'retired' && status !== 'parts_only';
}

/** Staleness threshold. A machine nobody has confirmed in this long is the
 *  drift signal the National Accounts Manager actually needs — today it is true
 *  of all 26 rows. */
export const STALE_DAYS = 90;

export function daysSinceConfirmed(lastConfirmedAt: string | null | undefined): number | null {
  if (!lastConfirmedAt) return null;
  const t = new Date(lastConfirmedAt).getTime();
  if (Number.isNaN(t)) return null;
  return (Date.now() - t) / 86_400_000;
}

/** true when the location has never been confirmed, or not for STALE_DAYS. */
export function isStale(lastConfirmedAt: string | null | undefined): boolean {
  const d = daysSinceConfirmed(lastConfirmedAt);
  return d === null || d > STALE_DAYS;
}

/** "never" / "3 days ago" / "on 12 May" — the phrasing used on cards. */
export function confirmedAgo(lastConfirmedAt: string | null | undefined): string {
  const d = daysSinceConfirmed(lastConfirmedAt);
  if (d === null) return 'never';
  if (d < 1) return 'today';
  if (d < 2) return 'yesterday';
  if (d < 45) return `${Math.floor(d)} days ago`;
  return new Date(lastConfirmedAt as string).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}
